import { isValid } from "date-fns";
import { TFunction } from "i18next";
import { IntlShape } from "react-intl";
import { formatDateLocale, normalizeDateToUtcMidnight } from "@/utils/helpers";

interface DateGenerationResult {
  success: boolean;
  dates: Date[];
  errors: Array<{ date: string; error: string }>;
}

interface DateEntry {
  date: Date;
  isRecurring: boolean;
}

const RECURRING_YEARS_COUNT = 10;

/** Normalized UTC date key for duplicate comparison (YYYY-MM-DD). */
export function getHolidayDateKey(date: Date): string {
  const normalized = normalizeDateToUtcMidnight(date);
  return normalized?.toISOString().split("T")[0] ?? "";
}

/**
 * Generates dates from form data with validation and recurring logic.
 * Allows both past and future dates to be entered.
 */
export function generateHolidayDates(
  dateEntries: DateEntry[],
  intl: IntlShape,
  t: TFunction
): DateGenerationResult {
  const generatedDates: Date[] = [];
  const errors: Array<{ date: string; error: string }> = [];

  dateEntries.forEach((dateEntry) => {
    const parsedDate = dateEntry.date;
    const isRecurring = dateEntry.isRecurring;
    const displayDate =
      parsedDate instanceof Date && isValid(parsedDate)
        ? formatDateLocale(intl, parsedDate)
        : "";

    if (!(parsedDate instanceof Date) || !isValid(parsedDate)) {
      errors.push({
        date: displayDate,
        error: t("addholiday.invaliddateformat"),
      });
      return;
    }

    // parsedDate is already normalized to UTC midnight by the date picker,
    // so its date components must be read with UTC accessors, not local ones.
    if (isRecurring) {
      const startYear = parsedDate.getUTCFullYear();
      const month = parsedDate.getUTCMonth();
      const day = parsedDate.getUTCDate();
      for (let i = 0; i < RECURRING_YEARS_COUNT; i++) {
        generatedDates.push(new Date(Date.UTC(startYear + i, month, day)));
      }
    } else {
      generatedDates.push(parsedDate);
    }
  });

  return {
    success: errors.length === 0,
    dates: generatedDates,
    errors,
  };
}
